import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { getAllVenueList, getAllSupervisorList } from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";

const TrainingForm = ({ register, errors, watch, setValue, clearErrors, control, selectedSupervisors, setSelectedSupervisors, isEditMode }) => {


    const [venueList, setVenueList] = useState([]);
    const [venueLoading, setVenueLoading] = useState(false);
    const [supervisorList, setSupervisorList] = useState([]);
    const [supervisorLoading, setSupervisorLoading] = useState(false);
    const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
    const [supervisorSearch, setSupervisorSearch] = useState("");
    // const [selectedSupervisors, setSelectedSupervisors] = useState([]);



    const navigate = useNavigate();
    const isAllDay = watch("allDay");
    const venueOptions = (venueList || []).map((item) => ({
        value: String(item?.id ?? ""),
        label: item?.venue_name ?? "",
    }));
    const repeatOptions = [
        { value: "none", label: "Do not repeat" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" }
    ];

    const selectMenuProps = {
        menuPlacement: "top",
        menuShouldScrollIntoView: false,
        menuPortalTarget: typeof document !== "undefined" ? document.body : null,
        menuPosition: "fixed",
        styles: {
            menuPortal: (base) => ({ ...base, zIndex: 9999 })
        }
    };

    useEffect(() => {
        fetchSportsList();
        fetchSupervisorList();
    }, []);

    useEffect(() => {
        if (isAllDay) {
            clearErrors(["startTime", "endTime"]);
            setValue("startTime", "");
            setValue("endTime", "");
        }
    }, [isAllDay, clearErrors, setValue]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(".supervisor-select")) {
                setShowSupervisorDropdown(false);
                setSupervisorSearch("");
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    // ================= SPORTS API =================

    const fetchSportsList = async () => {
        try {
            setVenueLoading(true);
            const response = await getAllVenueList();
            setVenueList(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching sports list:", error);
        } finally {
            setVenueLoading(false);
        }
    };

    const fetchSupervisorList = async () => {
        try {
            setSupervisorLoading(true);
            const response = await getAllSupervisorList();
            setSupervisorList(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching supervisor list:", error);
        } finally {
            setSupervisorLoading(false);
        }
    };

    const toggleSupervisor = (sup) => {

        const exists = selectedSupervisors.find(s => s.id === sup.id);

        let updated;

        if (exists) {
            updated = selectedSupervisors.filter(s => s.id !== sup.id);
        } else {
            updated = [...selectedSupervisors, sup];
        }

        setSelectedSupervisors(updated);
        // update react-hook-form value
        setValue(
            "supervisor",
            updated?.map(s => Number(s.id))
        );

        if (updated.length > 0) {
            clearErrors("supervisor");
        }

    };
    const getSupervisorName = (sup) =>
        [sup?.first_name, sup?.last_name].filter(Boolean).join(" ").trim() || "—";

    const getSupervisorRole = (sup) =>
        String(sup?.role_name ?? sup?.role ?? "").trim();

    const getSupervisorAtomId = (sup) => sup?.atom_id ?? sup?.atomId;

    const getSupervisorDisplayLabel = (sup) => {
        const parts = [getSupervisorName(sup)];
        const role = getSupervisorRole(sup);
        const atomId = getSupervisorAtomId(sup);
        if (role) parts.push(`(${role})`);
        if (atomId) parts.push(`#${atomId}`);
        return parts.join(" ");
    };

    const renderSupervisorLabel = (sup) => {
        const name = getSupervisorName(sup);
        const role = getSupervisorRole(sup);
        const atomId = getSupervisorAtomId(sup);

        return (
            <>
                <span className="supervisor-option-name">{name}</span>
                {role && <span className="supervisor-option-meta"> ({role})</span>}
                {atomId && <span className="supervisor-option-meta"> - {atomId}</span>}
            </>
        );
    };

    const matchesSupervisorSearch = (sup, query) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;

        const first = (sup?.first_name ?? "").toLowerCase();
        const last = (sup?.last_name ?? "").toLowerCase();
        const role = getSupervisorRole(sup).toLowerCase();
        const email = (sup?.email ?? "").toLowerCase();
        const atomId = String(getSupervisorAtomId(sup) ?? "").toLowerCase();
        const label = getSupervisorDisplayLabel(sup).toLowerCase();
        const qNoHash = q.replace(/^#/, "");

        return (
            label.includes(q) ||
            first.includes(q) ||
            last.includes(q) ||
            `${first} ${last}`.trim().includes(q) ||
            role.includes(q) ||
            email.includes(q) ||
            atomId.includes(qNoHash)
        );
    };

    const filteredSupervisorList = supervisorList.filter((sup) =>
        matchesSupervisorSearch(sup, supervisorSearch)
    );

    const removeSupervisor = (id) => {

        const updated = selectedSupervisors.filter(s => s.id !== id);
        setSelectedSupervisors(updated);
        setValue(
            "supervisor",
            updated?.map(s => Number(s.id))
        );

    };

    return (

        <div className="card p-4 mb-4">

            {/* Back button */}
            <div className="mb-3">
                <button
                    type="button"
                    className="btn back-btn"
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </button>
            </div>

            {/* TRAINING NAME */}
            <div className="mb-3">
                <label className="form-label">Training Name</label>

                <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Training Name"
                    {...register("trainingName")}
                />

                <p className="text-danger">{errors.trainingName?.message}</p>
            </div>


            {/* DATE + TIME */}
            <div className="row mb-3">

                {/* DATE */}
                <div className="col-md-2">
                    <label>Start Date</label>

                    <input
                        type="date"
                        className="form-control"
                        {...register("date")}
                    />

                    <p className="text-danger">{errors.date?.message}</p>
                </div>
                <div className="col-md-2">
                    <label>End Date</label>

                    <input
                        type="date"
                        className="form-control"
                        {...register("training_end_date")}
                    />

                    <p className="text-danger">{errors.training_end_date?.message}</p>
                </div>
                {/* START TIME */}
                <div className="col-md-2">
                    <label>Time</label>

                    <input
                        type="time"
                        disabled={isAllDay}
                        className="form-control"
                        {...register("startTime")}
                    />

                    <p className="text-danger">{errors.startTime?.message}</p>

                </div>

                {/* END TIME */}
                <div className="col-md-2">

                    <label style={{ visibility: "hidden" }}>End</label>

                    <input
                        type="time"
                        disabled={isAllDay}
                        className="form-control"
                        {...register("endTime")}
                    />

                    <p className="text-danger">{errors.endTime?.message}</p>
                </div>


                {/* ALL DAY */}
                <div className="col-md-3 d-flex align-items-center gap-2">

                    <div className="form-check">

                        <input
                            type="checkbox"
                            className="form-check-input"
                            {...register("allDay")}
                        />

                        <label className="form-check-label">
                            All Day
                        </label>

                    </div>

                </div>

            </div>


            {/* REPEAT + VENUE */}
            <div className="row mb-4">

                <div className="col-md-4 mb-2">

                    <label>Repeat</label>

                    <Controller
                        name="repeat"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...selectMenuProps}
                                options={repeatOptions}
                                value={
                                    repeatOptions.find((opt) => opt.value === field.value) ||
                                    repeatOptions[0]
                                }
                                onChange={(opt) => field.onChange(opt?.value ?? "none")}
                                isSearchable
                                placeholder="Select Repeat"
                                className="custom-react-select"
                            />
                        )}
                    />

                </div>

                <div className="col-md-4 mb-2">

                    <label>Venue</label>

                    <Controller
                        name="venue"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...selectMenuProps}
                                options={venueOptions}
                                value={
                                    venueOptions.find((opt) => opt.value === String(field.value)) ||
                                    null
                                }
                                onChange={(opt) => field.onChange(opt?.value ?? "")}
                                isLoading={venueLoading}
                                isDisabled={venueLoading}
                                isSearchable
                                placeholder="Select Venue"
                                className="custom-react-select"
                            />
                        )}
                    />
                </div>

            </div>

            {/* SUPERVISOR */}

            <div className="row mb-3">
                <div className="col-md-8 mb-3">

                    <label>Supervisor</label>

                    <div className="supervisor-select">
                        <input
                            type="hidden"
                            {...register("supervisor", {
                                validate: (value) =>
                                    selectedSupervisors.length > 0 || "Supervisor is required"
                            })}
                        />

                        {/* SELECTED CHIPS */}
                        <div
                            className="supervisor-input"
                            onClick={() => {
                                if (showSupervisorDropdown) setSupervisorSearch("");
                                setShowSupervisorDropdown(!showSupervisorDropdown);
                            }}
                        >

                            {selectedSupervisors?.length === 0 && (
                                <span className="">Select Supervisor</span>
                            )}

                            {selectedSupervisors?.map((sup) => (
                                <div key={sup?.id} className="supervisor-chip">

                                    <span title={getSupervisorDisplayLabel(sup)}>
                                        {renderSupervisorLabel(sup)}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSupervisor(sup?.id);
                                        }}
                                    >
                                        ×
                                    </button>

                                </div>
                            ))}

                        </div>

                        {/* DROPDOWN */}
                        {showSupervisorDropdown && (

                            <div className="supervisor-dropdown">

                                <div
                                    className="supervisor-search-wrap"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="text"
                                        className="form-control supervisor-search"
                                        placeholder="Search name, role, atom id..."
                                        value={supervisorSearch}
                                        onChange={(e) => setSupervisorSearch(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                </div>

                                {supervisorLoading ? (
                                    <div className="supervisor-option text-muted">Loading...</div>
                                ) : filteredSupervisorList.length === 0 ? (
                                    <div className="supervisor-option text-muted">No supervisors found</div>
                                ) : (
                                    filteredSupervisorList.map((sup) => (

                                        <label key={sup?.id} className="supervisor-option">

                                            <input
                                                type="checkbox"
                                                checked={selectedSupervisors.some(s => s?.id === sup?.id)}
                                                onChange={() => toggleSupervisor(sup)}
                                            />

                                            <span className="supervisor-option-label">
                                                {renderSupervisorLabel(sup)}
                                            </span>

                                        </label>

                                    ))
                                )}

                            </div>

                        )}

                    </div>
                    <p className="text-danger">{errors.supervisor?.message}</p>

                </div>
            </div>


            {/* DESCRIPTION */}
            <div className="mb-4">

                <label>Description</label>

                <textarea
                    rows="4"
                    className="form-control"
                    placeholder="Write your description here..."
                    {...register("description")}
                />

            </div>


            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">

                <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </button>

                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {isEditMode ? "Update" : "Create"}
                </button>

            </div>

        </div >

    );

};

export default TrainingForm;